import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../config/axios';
import { getActiveSystem, setActiveSystem, DEFAULT_SYSTEM } from '../config/system';
import { getActiveStack } from '../config/stack';
import { isBuiltInSystem, installSystemRegistry } from '../config/familyRegistry';

// Global game-system state: which system is active, the list of enabled
// systems (from /api/systems), and a setter. The active slug also lives at
// module scope (config/system.js) so the axios interceptor can read it.
const SystemContext = createContext({
  system: DEFAULT_SYSTEM,
  systems: [],
  setSystem: () => {},
  refreshSystems: () => {},
});

export const useSystem = () => useContext(SystemContext);

export function SystemProvider({ children }) {
  const [system, setSystemState] = useState(getActiveSystem());
  const [systems, setSystems] = useState([]);

  // MRCS Phase D. A system the bundle already describes is ready before this
  // component ever runs — `true` on the very first render, so `dnd-3.5` and
  // `pf-1e` see no state change, no extra render and no gate at all. A
  // user-created system's Families exist only in the database, so its metadata
  // is fetched once here and nothing renders until it lands: a page cannot
  // render a Family it has never heard of, and rendering "not found" first and
  // correcting it a moment later is worse than waiting for one request.
  //
  // Slice D5 added the SECOND reason to fetch, and it is the one that can
  // concern a built-in: a homebrew that adds a field to `dnd-3.5` is not in the
  // committed snapshot either, so the renderer would receive its value and draw
  // nothing. A system with a stack switched on therefore fetches too, and the
  // reply — resolved by the server for that stack — supersedes the snapshot for
  // as long as the stack is on. With no stack, `getActiveStack()` is null and
  // this is the expression it was.
  const [registryReady, setRegistryReady] = useState(
    () => isBuiltInSystem(getActiveSystem()) && !getActiveStack());
  useEffect(() => {
    if (registryReady) return;
    const slug = getActiveSystem();
    const forStack = Boolean(getActiveStack());
    apiClient.get('/api/registry', { params: { system: slug } })
      .then((res) => {
        // The reply names the system it is FOR, which is not always the one
        // asked about: the system middleware answers with the default for a
        // system the caller may not see. Installing it under the requested slug
        // would describe someone else's system as ours.
        if (res.data && res.data.slug === slug) installSystemRegistry(slug, res.data, { forStack });
      })
      .catch(() => { /* an unreachable registry degrades to the not-found page */ })
      .finally(() => setRegistryReady(true));
  }, [registryReady]);

  // Load enabled systems on boot, and again whenever a page says the reader's
  // access changed (a share link accepted, qa-runs 164) so the header menu
  // lists the new system without a reload. If the persisted choice is no
  // longer enabled (e.g. a system was disabled), fall back to the server's default.
  const [systemsVersion, setSystemsVersion] = useState(0);
  const refreshSystems = useCallback(() => setSystemsVersion((v) => v + 1), []);
  useEffect(() => {
    let cancelled = false;
    apiClient.get('/api/systems')
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.systems || [];
        setSystems(list);
        const enabled = list.map((s) => s.slug);
        if (list.length && !enabled.includes(getActiveSystem())) {
          const fallback = res.data?.default || DEFAULT_SYSTEM;
          setActiveSystem(fallback);
          setSystemState(fallback);
        }
      })
      .catch(() => { /* keep the current/default system if the call fails */ });
    return () => { cancelled = true; };
  }, [systemsVersion]);

  const setSystem = useCallback((slug) => {
    if (!slug || slug === getActiveSystem()) return;
    setActiveSystem(slug);
    setSystemState(slug);
    // Reload so every view refetches under the new system (components key their
    // fetches on the URL, not the system). Stay on the current page; only when
    // on a detail view (?slug=) drop to that entity's list, since the slug may
    // not exist in the new system — that's the one case where leaving is needed.
    const url = new URL(window.location.href);
    // A ?system= on the URL is authoritative on load (#92) — resolved detail
    // views stamp the ACTIVE system into it, so reloading without rewriting it
    // would silently revert the user's switch (config/system.js would read the
    // old slug back and re-persist it). Always name the NEW system in the URL
    // we reload with; this also keeps the reloaded URL self-describing.
    url.searchParams.set('system', slug);
    if (url.searchParams.has('slug')) {
      url.searchParams.delete('slug');
      url.searchParams.delete('rulebook'); // detail-pin param
    }
    window.location.assign(url.pathname + url.search);
  }, []);

  return (
    <SystemContext.Provider value={{ system, systems, setSystem, refreshSystems }}>
      {registryReady ? children : null}
    </SystemContext.Provider>
  );
}

export default SystemContext;
